import { extraInsuranceTooltipText } from "../../../../constants";
import {
  getAfterleaseVehicleCalculator,
  getNewVehicleCalculator
} from "../../../../services";
import type { CalculatorData } from "../types/calculatorData";
import { applyUrlParamsToCalculatorData } from "./applyUrlParamsToCalculatorData";
import { buildCalculationResultFromCalculatorData } from "./buildCalculationResult";
import {
  getActiveLeasingSelectionFromTree,
  getActiveRentSelectionFromTree
} from "./getActiveCalculatorSelection";
import {
  applyRentPackagePrices,
  buildAdditionalServiceItemsFromAdditions
} from "./additionalPackagesService";
import { getPackageValueForKind, PackageKind } from "./packageEnumUtils";

export async function fetchCalculatorDataForContact(auctionId: number, isAfterLease: boolean): Promise<CalculatorData | null> {
  const response = isAfterLease
    ? await getAfterleaseVehicleCalculator(auctionId)
    : await getNewVehicleCalculator(auctionId);

  if (response.status >= 200 && response.status <= 299 && response.data) {
    return response.data as CalculatorData;
  }
  return null;
}

export function buildContactFormCalculatorProperties(
  calculatorData: CalculatorData,
  encodedParamObject: Record<string, string>,
  offerDetails: any,
  literals: any,
  financingMethod: number,
  preferTreeSelection = false
) {
  const selectedPeriod = Number.parseInt(encodedParamObject.Period);
  const selectedMileage = Number.parseInt(encodedParamObject.Mileages);
  const selectedOws = Number.parseInt(encodedParamObject.Ows);
  const selectedRvs = Number.parseInt(encodedParamObject.Rvs);

  const appliedCalculator = preferTreeSelection
    ? (JSON.parse(JSON.stringify(calculatorData)) as CalculatorData)
    : applyUrlParamsToCalculatorData(calculatorData, {
        FinancingMethod: financingMethod,
        Period: encodedParamObject.Period,
        Ows: encodedParamObject.Ows,
        Mileages: encodedParamObject.Mileages,
        Rvs: encodedParamObject.Rvs
      });

  const activeRentSelection =
    financingMethod === 1 ? getActiveRentSelectionFromTree(appliedCalculator.RentPeriods) : null;
  const activeLeasingSelection =
    financingMethod === 2 ? getActiveLeasingSelectionFromTree(appliedCalculator.LeasingPeriods) : null;

  let selectedOptions: Record<string, any> = {};
  let resolvedPeriod = selectedPeriod;
  let resolvedOws = selectedOws;
  let resolvedMileage = selectedMileage;
  let resolvedRvs = selectedRvs;

  if (activeRentSelection) {
    selectedOptions = {
      Ows: activeRentSelection.ow,
      Periods: activeRentSelection.period,
      Mileages: activeRentSelection.mileage
    };
    resolvedPeriod = activeRentSelection.period.Value;
    resolvedOws = activeRentSelection.ow.Value;
    resolvedMileage = activeRentSelection.mileage.Value;
  } else if (activeLeasingSelection) {
    selectedOptions = {
      Ows: activeLeasingSelection.ow,
      Periods: activeLeasingSelection.period,
      Rvs: activeLeasingSelection.rv
    };
    resolvedPeriod = activeLeasingSelection.period.Value;
    resolvedOws = activeLeasingSelection.ow.Value;
    resolvedRvs = activeLeasingSelection.rv.Value;
  } else if (financingMethod === 1) {
    const periodGroup = appliedCalculator.RentPeriods.find((p) => p.Period.Value === selectedPeriod);
    const owGroup = periodGroup?.Ows.find((o) => o.Ow.Value === selectedOws);
    const mileage = owGroup?.Mileages.find((m) => m.Value === selectedMileage);
    selectedOptions = {
      Ows: owGroup?.Ow,
      Periods: periodGroup?.Period,
      Mileages: mileage
    };
  } else if (financingMethod === 2) {
    const periodGroup = appliedCalculator.LeasingPeriods.find((p) => p.Period.Value === selectedPeriod);
    const owGroup = periodGroup?.Ows.find((o) => o.Ow.Value === selectedOws);
    const rv = owGroup?.Rvs.find((r) => r.Value === selectedRvs);
    selectedOptions = {
      Ows: owGroup?.Ow,
      Periods: periodGroup?.Period,
      Rvs: rv
    };
  }

  const isAfterLease = encodedParamObject.AfterLease === "true";
  const isNewLeasing = !isAfterLease && financingMethod === 2;

  let packages: any[] = buildAdditionalServiceItemsFromAdditions(
    isNewLeasing ? appliedCalculator.LeasingAdditions : appliedCalculator.RentAdditions,
    literals,
    offerDetails.IsElectric,
    financingMethod,
    isAfterLease ? "afterLease" : "new",
    isAfterLease && financingMethod === 1 ? appliedCalculator.LeasingAdditions : undefined
  );

  if (!isAfterLease && !isNewLeasing) {
    const insurancePrice =
      appliedCalculator.Insurance?.find((ins) => ins.Period === resolvedPeriod)?.Value ?? undefined;
    packages = applyRentPackagePrices(
      packages,
      appliedCalculator,
      insurancePrice,
      appliedCalculator.InsuranceAdditionalText,
      "new"
    );
  }

  for (const item of packages) {
    if (encodedParamObject["Package" + item.Value] === "true") {
      item.isChecked = true;
    }
  }

  const filterToSend = {
    FinancingMethod: financingMethod,
    Filters: {
      AdditionalServices: packages.filter((p) => p.isChecked).map((p) => p.Value),
      Price: offerDetails.TotalPriceBrutto,
      HP: offerDetails.HP,
      isNew: !isAfterLease,
      isElectric: offerDetails.IsElectric,
      isTruck: offerDetails.IsTruck,
      AuctionId: Number(encodedParamObject.AuctionId),
      Period: resolvedPeriod,
      Ow: resolvedOws,
      Mileage: resolvedMileage,
      Mileages: resolvedMileage,
      Rv: resolvedRvs,
      Rvs: resolvedRvs,
      CalculatorLongTermTypeId: offerDetails.CalculatorLongTermTypeId,
      CalculatorLeasingTypeId: offerDetails.CalculatorLeasingTypeId
    }
  };

  const calculationResult =
    financingMethod === 3
      ? null
      : buildCalculationResultFromCalculatorData(appliedCalculator, filterToSend, !isAfterLease);

  const indexOfInsurance = packages.findIndex(
    (el) => el.Value === getPackageValueForKind(PackageKind.Insurance)
  );
  let packagesExtraTooltip = JSON.parse(JSON.stringify(packages));
  if (indexOfInsurance >= 0 && offerDetails.TotalPriceNetto > 325203.25) {
    packagesExtraTooltip[indexOfInsurance].Tooltip += extraInsuranceTooltipText;
  }
  if (indexOfInsurance >= 0 && calculationResult?.InsuranceAdditionalText) {
    packagesExtraTooltip[indexOfInsurance].Tooltip += ` ${calculationResult.InsuranceAdditionalText}`;
  }

  const financingMethodLabel =
    financingMethod === 1 ? "najem" : financingMethod === 2 ? "leasing" : "gotówka";

  return {
    AdditionalServices: packagesExtraTooltip,
    AuctionId: encodedParamObject.AuctionId,
    CalculationResult: calculationResult,
    FinancingMethod: {
      Name: financingMethodLabel,
      Status: "true",
      Value: financingMethod
    },
    ...selectedOptions
  };
}
